import type { SourceAuth, SourceId } from '../types.ts';
import { AuthExpired } from '../../lib/errors.ts';
import type { VeneraLoginRequest, VeneraLoginResult, VeneraRuntime } from './types.ts';
import { authFromPluginStorage } from './storage.ts';
import { normalizeVeneraError } from './errors.ts';

export async function runVeneraFormLogin(
  sourceId: SourceId,
  runtime: VeneraRuntime,
  request: VeneraLoginRequest,
  saveAuth?: (sourceId: SourceId, auth: SourceAuth, opts?: { username?: string }) => Promise<void>,
): Promise<VeneraLoginResult> {
  const auth = authFromPluginStorage(sourceId, {});
  try {
    const result = await runtime.call(
      ['__veneraPlugin', 'account', 'login'],
      [request.username, request.password],
      auth,
    );
    const username = loginUsername(result) ?? request.username;
    await saveAuth?.(sourceId, auth, { username });
    return { ok: true, auth, username };
  } catch (err) {
    throw normalizeVeneraError(err, sourceId);
  }
}

export async function validateVeneraAuth(
  sourceId: SourceId,
  runtime: VeneraRuntime,
  auth: SourceAuth,
): Promise<{ ok: boolean; username?: string; reason?: string }> {
  const snapshot = authFromSnapshot(auth);
  try {
    const value = await runtime.call(['__veneraPlugin', 'account', 'isLogged'], [], auth);
    return { ok: Boolean(value) || snapshot.ok, reason: Boolean(value) || snapshot.ok ? undefined : snapshot.reason };
  } catch (err) {
    const normalized = normalizeVeneraError(err, sourceId);
    if (normalized instanceof AuthExpired) return { ok: false, reason: normalized.message };
    return snapshot;
  }
}

function authFromSnapshot(auth: SourceAuth): { ok: boolean; reason?: string } {
  const store = auth.extra?.veneraPlugin;
  if (!store || typeof store !== 'object' || Array.isArray(store)) {
    return { ok: false, reason: 'Missing Venera plugin auth data.' };
  }
  const hasTokenLikeValue = Object.entries(store).some(([key, value]) => {
    const lower = key.toLowerCase();
    return (
      (lower.includes('token') || lower === 'uid') &&
      typeof value === 'string' &&
      value.trim().length > 0
    );
  });
  return hasTokenLikeValue ? { ok: true } : { ok: false, reason: 'Missing Venera plugin token.' };
}

function loginUsername(value: unknown): string | undefined {
  if (typeof value === 'string') return undefined;
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const username = (value as Record<string, unknown>).username;
  return typeof username === 'string' ? username : undefined;
}

